import { AnimatePresence, LayoutGroup, motion } from 'framer-motion'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Backdrop } from '@ds'
import { DustedText, useHowler, useUnlockAchievement } from '@shared'
import {
  AchievementSlug,
  AppLanguage,
  DialogHistoryItem,
  QuestId,
  useMainQuest,
  useMainQuestActions,
} from '@store'

import styles from './Dialog.module.scss'
import { AnswerItem } from './components/AnswerItem'
import { DialogItem } from './components/DialogItem'
import { Message } from './components/Message'
import { MessageName } from './components/MessageName'
import { easeInOutCubic, easingScrollTo } from './helpers'

type Props = {
  questDialog: DialogHistoryItem[] | null
  playerAvatar: string
  audioSrc: string
  questId: QuestId
  onClose: () => void
}

const renderDelayMs = 10
const baseDurationScrollMs = 500
const maxDurationScrollMs = 1500
const minDurationScrollMs = 300

export const Dialog: FC<Props> = ({
  questDialog,
  playerAvatar,
  audioSrc,
  questId,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [showAnswers, setShowAnswers] = useState(true)
  const prevMessagesLengthRef = useRef(questDialog?.length || 0)
  const isInitialScrollDoneRef = useRef(false)
  const isDialogComplete = useMainQuest.store.isDialogComplete()
  const { unlockAchievement } = useUnlockAchievement()
  const { t } = useTranslation()
  const { answerQuestDialog } = useMainQuestActions()
  const { i18n } = useTranslation()
  const [waitAnswer, setWaitAnswer] = useState(false);
  const isRussianLanguage = i18n.language === AppLanguage.RU

  const { audio } = useHowler({
    src: audioSrc,
    volume: 1,
    html5: true,
    preload: true,
  })

  useEffect(() => {
    const container = ref.current
    if (!container || !questDialog || questDialog.length === 0) {
      isInitialScrollDoneRef.current = false
      return
    }

    if (!isInitialScrollDoneRef.current) {
      const timer = setTimeout(() => {
        const firstMessage = container.querySelector<HTMLElement>(
          '[data-message-index="0"]'
        )
        if (firstMessage) {
          container.scrollTop = firstMessage.offsetTop - 54
        } else {
          container.scrollTop = 0
        }
        isInitialScrollDoneRef.current = true
      }, renderDelayMs)

      prevMessagesLengthRef.current = questDialog.length

      return () => clearTimeout(timer)
    }
  }, [questDialog])

  useEffect(() => {
    const container = ref.current
    if (!container || !questDialog) return

    const currentLength = questDialog.length
    const prevLength = prevMessagesLengthRef.current

    if (currentLength - prevLength <= 0) {
      return
    }

    const performSmoothScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const targetTop = scrollHeight - clientHeight
      const k = 1 + Math.abs(targetTop - scrollTop) / clientHeight

      let duration = Math.min(
        maxDurationScrollMs,
        Math.max(minDurationScrollMs, baseDurationScrollMs * k)
      )

      easingScrollTo(container, targetTop, duration, easeInOutCubic).then(
        () => {
          setShowAnswers(true)
        }
      )
    }

    const timer = setTimeout(performSmoothScroll, renderDelayMs)

    prevMessagesLengthRef.current = currentLength

    return () => clearTimeout(timer)
  }, [questDialog])

  const handleAnswerClick = async (answerId: string) => {
    if (waitAnswer) return;
    if (audio) {
      audio.stop()
    }
    setWaitAnswer(true)
    await answerQuestDialog(questId, answerId);
    setWaitAnswer(false)
    unlockAchievement(AchievementSlug.ONE_OF_THE_VOICES)
  }

  useEffect(() => {
    if (!audio) return

    audio.stop()
    audio.play()

    return () => {
      audio.stop()
    }
  }, [audio])


  const currentQuestion = useMemo(()=> {
    if (!questDialog || waitAnswer) return null;
    const lastQuestion = questDialog
    .slice()
    .reverse()
    .find((item) => item.type === 'question')

    if (!lastQuestion || lastQuestion.type !== 'question') return null;
    return lastQuestion.data
  },[questDialog, waitAnswer]);

  if (!questDialog || questDialog.length === 0) {
    return null
  }

  return (
    <Backdrop>
      <div className={styles.wrapper}>
        <div className={styles.container} ref={ref}>
          <div className={styles.itemsList}>
            {questDialog.map((item, index) => {
              if (item.type === 'question') {
                return (
                  <div key={`question-${index}`} data-message-index={index}>
                    <Message
                      id={`quest-dialog-message-${index}`}
                      message={{
                        messageType: 'base',
                        data: {
                          id: item.data.text,
                          text: isRussianLanguage
                            ? item.data.text
                            : item.data.text_en,
                          ...(item?.data.author && {
                            author: isRussianLanguage
                              ? item.data.author.name
                              : item.data.author.name_en,
                            avatar: item.data.author.avatar_url,
                          }),
                          isPlayer: false,
                          roleplay: isRussianLanguage
                            ? item.data.roleplay
                            : item.data.roleplay_en,
                        },
                      }}
                    />
                  </div>
                )
              } else {
                return (
                  <DialogItem
                    key={`answer-${index}`}
                    title={<MessageName name="You" />}
                    avatar={playerAvatar}
                  >
                    <DustedText
                      text={isRussianLanguage ? item.text : item.text_en}
                      hasInitAnimation={false}
                    />
                  </DialogItem>
                )
              }
            })}
            {currentQuestion && (
              <motion.div
                animate={{ opacity: showAnswers ? 1 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <DialogItem
                  key="choiceAnswer"
                  title={
                    <AnimatePresence mode="wait">
                      <motion.div
                        key="choose-answer"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, delay: 0.2 }}
                        className={styles.choiceAnswerTitle}
                      >
                        {t('shared.dialog.chooseYourAnswer')}
                      </motion.div>
                    </AnimatePresence>
                  }
                  avatar={playerAvatar}
                >
                  <div className={styles.answers}>
                    <LayoutGroup>
                      {isDialogComplete ? (
                        <>
                          <AnswerItem
                            key="close-dialog"
                            answer={{
                              id: 'close-dialog',
                              text: t('shared.close'),
                              nextId: '',
                            }}
                            isSelected={false}
                            isLast={true}
                            hidden={false}
                            selectedAnswerId={null}
                            onClick={onClose}
                          />
                        </>
                      ) : (
                        <>
                          {currentQuestion.choices?.map((choice, i) => {
                            const isLast =
                              i === (currentQuestion?.choices?.length || 0) - 1
                            return (
                              <AnswerItem
                                key={choice.id}
                                answer={{
                                  id: choice.id,
                                  text: isRussianLanguage
                                    ? choice.text
                                    : choice.text_en,
                                  nextId: '',
                                }}
                                isSelected={false}
                                isLast={isLast}
                                hidden={false}
                                selectedAnswerId={null}
                                onClick={(answer) =>
                                  handleAnswerClick(answer.id)
                                }
                              />
                            )
                          })}
                        </>
                      )}
                    </LayoutGroup>
                  </div>
                </DialogItem>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </Backdrop>
  )
}
